import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import z from 'zod'
import { toast } from 'sonner'
import {
    Boxes,
    Plus,
    Edit,
    Trash2,
    ToggleLeft,
    ToggleRight,
    Check,
} from 'lucide-react'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Badge } from '../components/ui/badge'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '../components/ui/dialog'
import { FormError } from '../components/ui/FormError'
import { Spinner } from '@/app/lib/loader/loader'
import { TableSkeleton } from '../components/ui/table-skeleton'
import { departmentService, Department } from '../services/department.service'
import { ConfirmationModal } from '../components/ui/ConfirmationModal'
import { GlobalPaginator } from '../components/ui/global-paginator'
import { useSessionStore } from '../store/auth/auth.store'
import { GlobalFilterBar } from '../components/ui/GlobalFilterBar'

// ─── Esquemas de validación ─────────────────────────────────────────────────

const departmentSchema = z.object({
    name: z.string().min(1, 'El nombre es requerido'),
    description: z.string().optional(),
    is_active: z.boolean(),
})

type DepartmentFormValidate = z.infer<typeof departmentSchema>

// ─── Component ────────────────────────────────────────────────────────────────

export function Departments() {
    const [departments, setDepartments] = useState<Department[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [searchQuery, setSearchQuery] = useState('')

    // Paginación
    const [pagination, setPagination] = useState({
        currentPage: 1,
        total: 0,
        perPage: 10,
        lastPage: 1,
        from: 0,
        to: 0,
    })

    // Modals state
    const [showModal, setShowModal] = useState(false)
    const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null)
    const [statusConfirmId, setStatusConfirmId] = useState<number | null>(null)
    const [editingId, setEditingId] = useState<number | null>(null)

    const {
        register,
        handleSubmit,
        watch,
        setValue,
        reset,
        formState: {
            errors,
            isSubmitting,
            isValid,
        }
    } = useForm<DepartmentFormValidate>({
        resolver: zodResolver(departmentSchema),
        mode: 'onChange',
        reValidateMode: 'onChange',
        defaultValues: {
            name: '',
            description: '',
            is_active: true,
        }
    })

    const isActiveValue = watch('is_active')
    const canCreateDepartment = useSessionStore((s) => s.canAction('inventory.department.create'))
    const canEditDepartment = useSessionStore((s) => s.canAction('inventory.department.edit'))
    const canDeleteDepartment = useSessionStore((s) => s.canAction('inventory.department.delete'))
    const canChangeDepartmentStatus = useSessionStore((s) => s.canAction('inventory.department.status'))

    // ─── Handlers ───────────────────────────────────────────────────────────────

    const fetchDepartments = async (page = 1, search = searchQuery) => {
        setIsLoading(true)
        try {
            const res = await departmentService.getDepartments({
                page,
                per_page: pagination.perPage,
                search,
            })

            const paginator = res.data

            setDepartments(paginator.data)

            setPagination({
                currentPage: paginator.current_page,
                total: paginator.total,
                perPage: paginator.per_page,
                lastPage: paginator.last_page,
                from: paginator.from,
                to: paginator.to,
            })
        } catch (error) {
            toast.error('Error al cargar los departamentos')
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        fetchDepartments(pagination.currentPage, searchQuery)
    }, [pagination.currentPage, searchQuery])

    

    const openCreateModal = () => {
        if (!canCreateDepartment) return
        setEditingId(null)
        reset({
            name: '',
            description: '',
            is_active: true,
        })
        setShowModal(true)
    }

    const openEditModal = (department: Department) => {
        if (!canEditDepartment) return
        setEditingId(department.id)
        reset({
            name: department.name,
            description: department.description || '',
            is_active: department.is_active,
        })
        setShowModal(true)
    }

    const handleCloseModal = () => {
        setShowModal(false)
        setEditingId(null)
        reset()
    }

    const onSubmit = async (data: DepartmentFormValidate) => {
        if (editingId && !canEditDepartment) return
        if (!editingId && !canCreateDepartment) return

        try {
            const payload = {
                ...data,
            }

            if (editingId) {
                await departmentService.updateDepartment(editingId, payload)
                toast.success('Departamento actualizado exitosamente')
            } else {
                await departmentService.createDepartment(payload)
                toast.success('Departamento creado exitosamente')
            }
            handleCloseModal()
            fetchDepartments()
        } catch (error) {
            toast.error('Ocurrió un error al guardar el departamento')
        }
    }

    // ─── Eliminar Registro  ──────────────────────────────────────
    const executeDelete = async () => {
        if (!deleteConfirmId) return;
        if (!canDeleteDepartment) return;
        try {
            await departmentService.deleteDepartment(deleteConfirmId)
            toast.success('Departamento eliminado exitosamente')
            fetchDepartments()
        } catch (error) {
            toast.error("Error", { description: "No se pudo eliminar el departamento." });
        } finally {
            setDeleteConfirmId(null); // Cerramos el modal bonito
        }
    };
    // ─── Cambiar estado de Registro ──────────────────────────────────────
    const executeChangeStatus = async () => {
        if (!statusConfirmId) return;
        if (!canChangeDepartmentStatus) return;
        try {
            await departmentService.updateStatus(statusConfirmId)
            toast.success('Estado del departamento actualizado exitosamente')
            fetchDepartments()
        } catch (error) {
            toast.error("Error", { description: "No se pudo actualizar el estado del departamento." });
        } finally {
            setStatusConfirmId(null); // Cerramos el modal bonito
        }
    };

    // ─── Render ──────────────────────────────────────────────────────────────────

    return (
        <div className="space-y-6 p-6">
            {/* Header & Filters */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-slate-900">Departamento</h1>
                    <p className="mt-1 text-sm text-slate-600">
                        Administra los departamentos de los productos.
                    </p>
                </div>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                    
                    {canCreateDepartment && (
                        <Button
                            onClick={openCreateModal}
                            className="bg-primary hover:bg-primary/90 w-full sm:w-auto"
                        >
                            <Plus className="mr-2 h-4 w-4" />
                            Nuevo Departamento
                        </Button>
                    )}
                </div>
            </div>
            <GlobalFilterBar
    show={['search']}
    values={{
        search: searchQuery,
    }}
    onChange={(changes) => {
        if (changes.search !== undefined) {
            setSearchQuery(changes.search)
            setPagination(prev => ({
                ...prev,
                currentPage: 1,
            }))
        }
    }}
    customOptions={{
        searchPlaceholder: 'Buscar departamento...',
    }}
/>

            {/* Table */}
            {isLoading ? (
                <TableSkeleton
                    headers={['Nombre', 'Descripción', 'Fecha de Actualización', 'Estado', 'Acciones']}
                    columns={['text', 'text-two-lines', 'text', 'badge', 'actions']}
                    rows={5}
                />
            ) : (
                <div className="overflow-hidden rounded-lg border border-border bg-table-bg text-table-foreground shadow-sm">
                    <div className="overflow-x-auto">
                        <table className="w-full whitespace-nowrap">
                            <thead className="border-b border-border bg-table-header-bg text-table-header-foreground">
                                <tr >
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider  text-table-header-foreground">
                                        Nombre
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider  text-table-header-foreground">
                                        Descripción
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider  text-table-header-foreground">
                                        Fecha de Actualización
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider  text-table-header-foreground">
                                        Estado
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider  text-table-header-foreground">
                                        Acciones
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border bg-table-bg text-table-foreground">
                                {departments.length > 0 ? (
                                    departments.map((department) => (
                                        <tr key={department.id} className="hover:bg-table-row-hover">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-3">
                                                    <div>
                                                        <p className="font-medium text-table-foreground">{department.name}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex flex-col gap-1 text-sm text-slate-600">
                                                    {department.description && (
                                                        <span className="flex items-center gap-1.5 text-table-foreground">
                                                            {department.description}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-table-foreground">
                                                {department.updated_at ? department.updated_at.split('T')[0] : ''}
                                            </td>
                                            <td className="px-6 py-4">
                                                <Badge
                                                    className={
                                                        department.is_active
                                                        ? 'bg-[#DCFCE6] text-[#008438]'
                                                        : 'bg-[#FFE2E2] text-[#C10008]'
                                                    }
                                                    >
                                                    {department.is_active ? 'Activo' : 'Inactivo'}
                                                    </Badge>
                                                                        </td>
                                            <td className="px-6 py-4 text-right">
                                                <div className="flex items-center justify-end gap-2">
                                                    <button
                                                        hidden={!canChangeDepartmentStatus}
                                                        onClick={() => setStatusConfirmId(department.id)}
                                                        title={department.is_active ? 'Desactivar' : 'Activar'}
                                                        className={`rounded p-1.5 transition-colors ${department.is_active
                                                             ? 'text-primary hover:bg-primary/10'
                                                             : 'text-table-foreground hover:bg-muted'
                                                            }`}
                                                    >
                                                        {department.is_active ? (
                                                            <ToggleRight className="h-5 w-5" />
                                                        ) : (
                                                            <ToggleLeft className="h-5 w-5" />
                                                        )}
                                                    </button>
                                                    <button
                                                        hidden={!canEditDepartment}
                                                        onClick={() => openEditModal(department)}
                                                         title="Editar"
                                                        className="rounded p-1.5 text-table-foreground transition-colors hover:bg-muted"
                                                    >
                                                        <Edit className="h-4 w-4" />
                                                    </button>
                                                    <button
                                                        hidden={!canDeleteDepartment}
                                                        onClick={() => setDeleteConfirmId(department.id)}
                                                        title="Eliminar"
                                                        className="rounded p-1.5 text-red-600 transition-colors hover:bg-red-50"
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={5} className="px-6 py-12 text-center">
                                            <Boxes className="mx-auto mb-3 h-8 w-8 text-slate-300" />
                                            <p className="text-slate-500">No se encontraron departamentos</p>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    {/* Controles de Paginacion */}
                    {departments.length > 0 && (
                        <GlobalPaginator
                            totalItems={pagination.total}
                            itemsPerPage={pagination.perPage}
                            currentPage={pagination.currentPage}
                            from={pagination.from}
                            to={pagination.to}
                            onPageChange={(page) =>
                                setPagination(prev => ({
                                    ...prev,
                                    currentPage: page,
                                }))
                            }
                        />
                    )}
                </div>
            )}

            {/* ═══════════════════════════════════════════════════════════
          MODAL: Crear / Editar Departamento
      ══════════════════════════════════════════════════════════════ */}
            <Dialog open={showModal} onOpenChange={setShowModal}>
                <DialogContent className="sm:max-w-md">
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-lg font-semibold text-card-foreground">
                             <Boxes className="h-5 w-5 text-primary" />
                                {editingId ? 'Editar Departamento' : 'Nuevo Departamento'}
                        </DialogTitle>

                 <DialogDescription className="text-card-description">
                             {editingId
                             ? 'Modifica los datos del departamento existente.'
                            : 'Ingresa los datos para registrar un nuevo departamento.'}
                    </DialogDescription>
                </DialogHeader>
                        <div className="space-y-4 py-4">
                            <div className="space-y-2">
                                <label className="mb-1 block text-sm font-medium text-card-foreground">
                                    Nombre del departamento <span className="text-red-500">*</span>
                                </label>
                                <Input
                                    placeholder="Ej. Analgesicos"
                                    {...register('name')}
                                />
                                <FormError message={errors.name?.message} />
                            </div>

                            <div className="space-y-2">
                                <label className="mb-1 block text-sm font-medium text-card-foreground">
                                    Descripción
                                </label>
                                <Input
                                    placeholder="Descripción del departamento"
                                    {...register('description')}
                                />
                                <FormError message={errors.description?.message} />
                            </div>

                            <div hidden={!canChangeDepartmentStatus} className="flex items-center gap-2 pt-2">
                                <button
                                    type="button"
                                    role="switch"
                                    aria-checked={isActiveValue}
                                    onClick={() => setValue('is_active', !isActiveValue)}
                                    className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none ${isActiveValue ? 'bg-blue-400' : 'bg-slate-200'
                                        }`}
                                >
                                    <span
                                        aria-hidden="true"
                                        className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${isActiveValue ? 'translate-x-2' : '-translate-x-2'
                                            }`}
                                    />
                                </button>
                                <span className="mb-1 block text-sm font-medium text-card-foreground">Departamento Activo</span>
                            </div>
                        </div>

                        <DialogFooter className="gap-2 sm:justify-end">
                            <Button type="button" variant="outline" onClick={handleCloseModal} disabled={isSubmitting}>
                                Cancelar
                            </Button>
                            <Button
                                type="submit"
                                disabled={isSubmitting}
                                className="bg-primary hover:bg-primary/90"
                            >
                                {isSubmitting ? (
                                    <Spinner className="mr-2 h-4 w-4" />
                                ) : (
                                    <Check className="mr-2 h-4 w-4" />
                                )}
                                Guardar
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>


            {/* ═══════════════════════════════════════════════════════════
                      MODALS DE CONFIRMACIÓN
                  ══════════════════════════════════════════════════════════════ */}
            <ConfirmationModal
                isOpen={deleteConfirmId !== null}
                onClose={() => setDeleteConfirmId(null)}
                onConfirm={executeDelete}
                preset="delete"
                itemName="departamento"
            />
            <ConfirmationModal
                isOpen={statusConfirmId !== null}
                onClose={() => setStatusConfirmId(null)}
                onConfirm={executeChangeStatus}
                preset="toggleStatus"
                itemName="departamento"
            />
        </div>
    )
}
